import { QueryKey } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useUnsafeCurrentOrganizationId, useUnsafeCurrentWorkspaceId } from '~/hooks/app';

const queryKeyFormatter = <T extends Record<string, unknown>, K extends QueryKey = QueryKey>(
  prefix: T,
  queryKey: K,
) => [prefix, ...queryKey];

type OrganizationKeyPrefix = { organizationId: string };
type WorkspaceKeyPrefix = { organizationId: string; workspaceId: string };

export const organizationQueryKey = queryKeyFormatter<OrganizationKeyPrefix>;
export const workspaceQueryKey = queryKeyFormatter<WorkspaceKeyPrefix>;

/**
 * Organization query info
 */
export const useCurrentOrganizationQueryKey = <T extends QueryKey>() => {
  const organizationId: string = useUnsafeCurrentOrganizationId();

  const keyFormatter = useCallback(
    (queryKey: T) => queryKeyFormatter<OrganizationKeyPrefix, T>({ organizationId }, queryKey),
    [organizationId],
  );

  return {
    queryKeyFormatter: keyFormatter,
    organizationId,
  };
};

/**
 * Workspace query info
 */
export const useCurrentWorkspaceQueryKey = <T extends QueryKey>() => {
  const organizationId: string = useUnsafeCurrentOrganizationId();
  const workspaceId: string = useUnsafeCurrentWorkspaceId();

  const keyFormatter = useCallback(
    (queryKey: T) =>
      queryKeyFormatter<WorkspaceKeyPrefix, T>({ organizationId, workspaceId }, queryKey),
    [organizationId, workspaceId],
  );

  return {
    queryKeyFormatter: keyFormatter,
    organizationId,
    workspaceId,
  };
};

/**
 * Query key element order matters when keys get hashed.
 *
 * To not trigger needles re-fetching when order ir parameters changes move array keys to object
 * where order doesn't matter
 */
export const deterministicallyHashedKeys = <T extends string | number = string>(keys: T[] = []) =>
  keys.reduce<Record<T, true>>(
    (acc, item) => {
      acc[item] = true;
      return acc;
    },
    {} as Record<T, true>,
  );
